import { useEffect, useState } from 'react';

/**
 * Tracks which landing section currently occupies the reading band just below
 * the header, so the nav can show where the visitor actually is.
 *
 * Uses a rootMargin band rather than scroll math: the browser does the
 * intersection work off the main thread, and there is no listener firing on
 * every scroll frame.
 */
const useActiveSection = (sectionIds: string[], enabled: boolean) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') {
      setActiveId(null);
      return;
    }

    const nodes = sectionIds
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => node !== null);

    if (nodes.length === 0) return;

    const visible = new Map<string, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        });

        // Near the very top the hero always wins, regardless of what the band
        // reports — otherwise the first nav item never lights up on load.
        if (window.scrollY < 80) {
          setActiveId(sectionIds[0]);
          return;
        }

        let best: string | null = null;
        let bestRatio = 0;
        visible.forEach((ratio, id) => {
          if (ratio >= bestRatio) {
            bestRatio = ratio;
            best = id;
          }
        });

        if (best) setActiveId(best);
      },
      {
        // A band running from under the header down to 45% of the viewport.
        rootMargin: '-88px 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionIds, enabled]);

  return activeId;
};

export default useActiveSection;
